import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Bell, ExternalLink, Trash2, Clock3 } from "lucide-react";
import { toast } from "sonner";

export default function History() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/searches");
      setItems(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const remove = async (id) => {
    try {
      await api.delete(`/searches/${id}`);
      setItems((x) => x.filter((i) => i.id !== id));
      toast.success("Recherche supprimée");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const toggleAlert = async (id) => {
    try {
      const { data } = await api.post(`/searches/${id}/price-alert`);
      setItems((arr) =>
        arr.map((i) => (i.id === id ? { ...i, price_alert: data.price_alert } : i))
      );
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <main className="max-w-[1400px] mx-auto px-6 md:px-10 py-10">
      <div className="mb-8">
        <div className="tape-label text-poppy">JOURNAL</div>
        <h1 className="font-display font-black text-4xl md:text-5xl">Historique</h1>
        <p className="text-ink/60 mt-1">Toutes vos recherches précédentes.</p>
      </div>

      {loading ? (
        <div className="text-ink/50">Chargement…</div>
      ) : items.length === 0 ? (
        <div className="border border-dashed border-ink/30 p-12 text-center">
          <Clock3 className="w-8 h-8 mx-auto text-poppy" />
          <div className="font-display font-black text-2xl mt-3">
            Aucune recherche
          </div>
          <p className="text-ink/60 mt-1">
            Lancez une recherche depuis la page d'accueil.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((s) => (
            <article
              key={s.id}
              data-testid={`history-${s.id}`}
              className="card-brut overflow-hidden"
            >
              {s.thumbnail_b64 && (
                <img
                  src={`data:image/jpeg;base64,${s.thumbnail_b64}`}
                  alt={s.analysis?.title}
                  className="w-full aspect-[4/3] object-cover border-b border-ink/10"
                />
              )}
              <div className="p-4 space-y-2">
                <div className="flex items-center justify-between">
                  <span className="tape-label text-poppy">{s.source_type}</span>
                  <span className="text-[10px] tape-label text-ink/40">
                    {new Date(s.created_at).toLocaleDateString("fr-FR")}
                  </span>
                </div>
                <div className="font-display font-bold text-lg leading-tight line-clamp-2">
                  {s.analysis?.title}
                </div>
                <div className="text-xs text-ink/60 line-clamp-2">
                  {s.analysis?.search_keywords}
                </div>
                <div className="flex items-center gap-2 pt-1">
                  <button
                    onClick={() => toggleAlert(s.id)}
                    data-testid={`history-alert-${s.id}`}
                    className={`flex-1 px-2.5 py-2 text-xs flex items-center justify-center gap-1.5 border border-ink font-bold ${
                      s.price_alert ? "bg-electric" : "bg-white hover:bg-mist/40"
                    }`}
                  >
                    <Bell className="w-3.5 h-3.5" />
                    {s.price_alert ? "Alerte ON" : "Alerte"}
                  </button>
                  <button
                    onClick={() => setOpen(open === s.id ? null : s.id)}
                    className="flex-1 btn-brut px-2.5 py-2 text-xs"
                  >
                    {open === s.id ? "Masquer" : "Voir magasins"}
                  </button>
                  <button
                    data-testid={`history-delete-${s.id}`}
                    onClick={() => remove(s.id)}
                    className="p-2 border border-ink/30 hover:bg-ink hover:text-linen"
                    aria-label="Supprimer"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
                {open === s.id && (
                  <div className="pt-2 grid grid-cols-2 gap-1.5">
                    {s.merchants.map((m) => (
                      <a
                        key={m.id}
                        href={m.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="px-2 py-1.5 border border-ink text-[11px] font-bold flex items-center justify-between hover:bg-ink hover:text-linen"
                      >
                        {m.name} <ExternalLink className="w-3 h-3" />
                      </a>
                    ))}
                  </div>
                )}
              </div>
            </article>
          ))}
        </div>
      )}
    </main>
  );
}
